import { Info } from 'lucide-react'
import { useMemo, useState } from 'react'
import { useTableColumns } from '@/hooks/useTableColumns'
import { useSortedRows } from '@/hooks/useSortedRows'
import type { SortAccessors } from '@/lib/tableSort'
import type { TableColumnDef } from '@/lib/tableColumnPrefs'
import { ResizableTh } from '@/components/table'
import { cn } from '@/lib/utils'
import type { EquipmentReservation } from '@/api/processStepsApi'
import { CalendarCell, CycleCell } from './gridCells'
import { ReturnToServiceDialog } from './ReturnToServiceDialog'
import {
  CYCLE_LABELS,
  CYCLE_ORDER,
  EQUIPMENT_PURPOSE_LABELS,
  type Cycle,
  type Equipment,
  type ResolveEventMeta,
} from '@/types/equipment'
import {
  EQUIPMENT_SCOPE,
  ROOM_SCOPE,
  STORAGE_SCOPE,
  useEquipmentScope,
  type EquipmentCopy,
  type EquipmentScope,
} from './equipmentScope'

const HEAD_CELL = 'whitespace-nowrap px-3 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wide'

// Per-user column layout. Equipment ID is locked — it identifies the unit.
// See src/components/table/index.ts for the pattern. One layout per page: the
// Cleaning page's room grid keeps its own column choices (z8nrz7ewt3).
const tableIdFor = (scope: EquipmentScope) => `${scope.tablePrefix}-grid`

/** Column id for one service cycle. Namespaced so a cycle key can never
 *  collide with a fixed column's id. */
function cycleColumnId(cycle: Cycle): string {
  return `cycle:${cycle}`
}

const columnsFor = (copy: EquipmentCopy): TableColumnDef[] => [
  { id: 'equipmentId', header: copy.unitId, locked: true, sortable: true },
  { id: 'equipmentType', header: copy.type, sortable: true },
  { id: 'group', header: copy.group, sortable: true },
  { id: 'purpose', header: 'Purpose', sortable: true },
  { id: 'info', header: 'Info' },
  { id: 'location', header: 'Location', sortable: true },
  { id: 'availability', header: 'Availability', sortable: true },
  ...CYCLE_ORDER.map(c => ({ id: cycleColumnId(c), header: CYCLE_LABELS[c] })),
  { id: 'calendar', header: 'Calendar' },
]

// Built once per page, not per render: the column-prefs hook keys its memo on
// the array's identity.
const COLUMNS = {
  equipment: columnsFor(EQUIPMENT_SCOPE.copy),
  room: columnsFor(ROOM_SCOPE.copy),
  storage: columnsFor(STORAGE_SCOPE.copy),
} satisfies Record<EquipmentScope['variant'], TableColumnDef[]>

// The per-column extras layered over HEAD_CELL: the cycle block and Calendar
// carry the divider that separates service from identity, and Info centres.
const EQUIPMENT_HEAD_EXTRA: Record<string, string | undefined> = {
  info: 'text-center',
  calendar: 'border-l border-white/20',
  ...Object.fromEntries(CYCLE_ORDER.map(c => [cycleColumnId(c), 'border-l border-white/20'])),
}

// Header sorting (z8nrz7e4h6). Cycle and Calendar cells are schedule widgets,
// not values, so they stay unsortable. Availability is added in the component —
// it depends on the live holds.
const EQUIPMENT_SORT: SortAccessors<Equipment> = {
  equipmentId: eq => eq.equipmentId,
  equipmentType: eq => eq.equipmentType,
  group: eq => eq.group,
  purpose: eq => EQUIPMENT_PURPOSE_LABELS[eq.purpose],
  location: eq => eq.location.display,
}

/** The availability pill's wording, minus its time — what the column sorts by. */
function availabilityLabel(
  equipment: Equipment,
  reservations: EquipmentReservation[],
  unavailable: boolean,
  now: number,
): string | null {
  if (equipment.status === 'OUT_OF_SERVICE') return 'Out of service'
  if (unavailable) return null
  const mine = reservations.filter((r) => r.equipmentId === equipment.id)
  if (mine.some((r) => new Date(r.startsAt).getTime() <= now && new Date(r.endsAt).getTime() > now)) return 'In use'
  if (mine.some((r) => new Date(r.startsAt).getTime() > now)) return 'Booked'
  return 'Available'
}

/** "Out of service", as a link to put the unit back in service. */
function OutOfServiceStatus({ equipment }: { equipment: Equipment }) {
  const [open, setOpen] = useState(false)
  return (
    <>
      <button
        type='button'
        onClick={() => setOpen(true)}
        title='Return to service'
        className='inline-flex rounded-full bg-red-50 px-2 py-0.5 text-[11px] font-medium text-red-700 underline underline-offset-2 hover:text-red-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-300'
      >
        Out of service
      </button>
      <ReturnToServiceDialog
        unit={open ? { id: equipment.id, label: equipment.equipmentId } : null}
        onClose={() => setOpen(false)}
      />
    </>
  )
}

/**
 * What a unit is doing right now: out of service, held by a process step, or
 * free. Process-step bookings occupy the same machines as maintenance, so the
 * equipment list has to show them — otherwise someone reads "available" here
 * and is refused at booking.
 */
function AvailabilityCell({
  equipment,
  reservations,
  unavailable,
}: {
  equipment: Equipment
  reservations: EquipmentReservation[]
  /** The holds query failed — every unit would otherwise read as free. */
  unavailable: boolean
}) {
  // Checked first: out-of-service is a property of the unit itself, so it is
  // still known and still worth showing when the holds query is down.
  // The one status a person can act on here (z8nrz7fzyk): it opens the
  // signed return to service. No other status is a link.
  if (equipment.status === 'OUT_OF_SERVICE') return <OutOfServiceStatus equipment={equipment} />
  // "Available" here is derived from an empty hold list, which is exactly what
  // a failed read produces — say nothing rather than say "free" wrongly.
  if (unavailable) {
    return <span className='text-[11px] text-gray-400'>Availability unavailable</span>
  }
  const now = Date.now()
  const mine = reservations.filter((r) => r.equipmentId === equipment.id)
  const current = mine.find(
    (r) => new Date(r.startsAt).getTime() <= now && new Date(r.endsAt).getTime() > now,
  )
  if (current) {
    return (
      <span
        className='inline-flex rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-medium text-amber-700'
        title={`Reserved until ${new Date(current.endsAt).toLocaleString()}`}
      >
        In use until {new Date(current.endsAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
      </span>
    )
  }
  const next = mine
    .filter((r) => new Date(r.startsAt).getTime() > now)
    .sort((a, b) => a.startsAt.localeCompare(b.startsAt))[0]
  if (next) {
    return (
      <span
        className='inline-flex rounded-full bg-blue-50 px-2 py-0.5 text-[11px] font-medium text-blue-700'
        title={`Reserved ${new Date(next.startsAt).toLocaleString()} → ${new Date(next.endsAt).toLocaleString()}`}
      >
        Booked {new Date(next.startsAt).toLocaleDateString()}
      </span>
    )
  }
  return <span className='text-[11px] text-gray-400'>Available</span>
}

/**
 * The FULL unit list in the order this grid's headers chose (z8nrz7e4h6).
 *
 * The planner pages the list before handing it to the grid, so a sort applied
 * only inside the grid would order just the fifteen rows on screen. The page
 * calls this on the whole list first, with the same table id and accessors as
 * the grid, and pages the result. `sortKey` changes whenever the header sort
 * does, so the page can return to page 1.
 */
export function useEquipmentGridSort(
  rows: Equipment[],
  reservations: EquipmentReservation[],
  reservationsUnavailable: boolean,
): { rows: Equipment[]; sortKey: string } {
  const scope = useEquipmentScope()
  const cols = useTableColumns(tableIdFor(scope), COLUMNS[scope.variant])
  const accessors = useMemo<SortAccessors<Equipment>>(() => {
    const now = Date.now()
    return {
      ...EQUIPMENT_SORT,
      availability: eq => availabilityLabel(eq, reservations, reservationsUnavailable, now),
    }
  }, [reservations, reservationsUnavailable])
  const sorted = useSortedRows(rows, cols.sort, accessors)
  return { rows: sorted, sortKey: cols.sort ? `${cols.sort.id}:${cols.sort.dir}` : '' }
}

export function EquipmentGrid({
  rows,
  resolveMeta,
  onInfo,
  onConfigure,
  onCalendar,
  reservations = [],
  reservationsUnavailable = false,
}: {
  rows: Equipment[]
  resolveMeta: ResolveEventMeta
  onInfo: (eq: Equipment) => void
  onConfigure: (eq: Equipment, cycle: Cycle) => void
  onCalendar: (eq: Equipment) => void
  /** Live process-step holds in the visible window. */
  reservations?: EquipmentReservation[]
  /** The holds query failed, so an empty `reservations` means "unknown", not "free". */
  reservationsUnavailable?: boolean
}) {
  const scope = useEquipmentScope()
  const cols = useTableColumns(tableIdFor(scope), COLUMNS[scope.variant])
  // Header sort is applied on top of the order the page hands in (the
  // toolbar's sort dropdown); clearing it returns to that order (z8nrz7e4h6).
  const sortAccessors = useMemo<SortAccessors<Equipment>>(() => {
    const now = Date.now()
    return {
      ...EQUIPMENT_SORT,
      availability: eq => availabilityLabel(eq, reservations, reservationsUnavailable, now),
    }
  }, [reservations, reservationsUnavailable])
  const sortedRows = useSortedRows(rows, cols.sort, sortAccessors)
  // How many of the service-cycle columns plus Calendar are on screen. The
  // "not maintained" row replaces that whole block with one spanning cell, so
  // the span has to follow what the user has left visible.
  const serviceSpan =
    CYCLE_ORDER.filter(c => cols.isVisible(cycleColumnId(c))).length +
    (cols.isVisible('calendar') ? 1 : 0)
  return (
    <>
    <div className='overflow-x-persistent rounded-lg border border-gray-200'>
      <table className='w-full min-w-[1400px] border-collapse text-sm'>
        <thead className='sticky top-0 z-10 bg-primary text-primary-foreground'>
          <tr>
            {cols.visible.map(({ def }) => (
              <ResizableTh
                key={def.id}
                columnId={def.id}
                columns={cols}
                label={def.header}
                className={cn(HEAD_CELL, EQUIPMENT_HEAD_EXTRA[def.id])}
              >
                {def.header}
              </ResizableTh>
            ))}
          </tr>
        </thead>
        <tbody>
          {sortedRows.map((eq, i) => (
            <tr
              key={eq.id}
              className={cn(
                'border-b border-gray-100 transition-colors hover:bg-blue-50/60',
                i % 2 === 1 && 'bg-gray-50/60',
              )}
            >
              {/* Guarded cell by cell rather than through ColumnCells: the
                  "not maintained" branch below replaces the whole cycle block
                  with ONE spanning cell, which a one-id-per-cell map cannot
                  express. */}
              {cols.isVisible('equipmentId') && (
                <td className='px-3 py-2.5 align-top font-mono text-xs font-semibold text-gray-800'>
                  <span className='inline-flex items-center gap-2.5'>
                    {eq.equipmentId}
                    {/* A room on no floor yet (Cleaning Visual, z8nrz7f731):
                        the red mark the spec asks for, until it is mapped in
                        Building set-up. Null only — absent means mock data. */}
                    {scope.variant === 'room' && eq.floorId === null && (
                      <span
                        role='img'
                        aria-label='Not mapped to a floor'
                        title='Not mapped to a floor. Place it in Building set-up.'
                        className='inline-block h-[13px] w-[13px] shrink-0 border-2 border-red-600'
                      />
                    )}
                  </span>
                </td>
              )}
              {cols.isVisible('equipmentType') && (
                <td className='px-3 py-2.5 align-top text-xs text-gray-700'>{eq.equipmentType}</td>
              )}
              {cols.isVisible('group') && (
                <td className='px-3 py-2.5 align-top text-xs text-gray-500'>{eq.group}</td>
              )}
              {cols.isVisible('purpose') && (
              <td className='px-3 py-2.5 align-top'>
                {/* A pill, not plain text: purpose is a two-value axis someone
                    scans the column for, and a chip is findable at a glance
                    where a fourth grey string in a row of grey strings is not. */}
                <span
                  className={cn(
                    'inline-flex rounded px-1.5 py-0.5 text-[11px] font-semibold',
                    eq.purpose === 'STORAGE'
                      ? 'bg-indigo-50 text-indigo-700'
                      : 'bg-gray-100 text-gray-600',
                  )}
                >
                  {EQUIPMENT_PURPOSE_LABELS[eq.purpose]}
                </span>
              </td>
              )}
              {cols.isVisible('info') && (
              <td className='px-3 py-2.5 text-center align-top'>
                <button
                  type='button'
                  onClick={() => onInfo(eq)}
                  title={`View ${scope.copy.unitLower} details`}
                  className='inline-flex items-center justify-center text-gray-400 transition-colors hover:text-blue-600'
                >
                  <Info className='h-4 w-4' />
                </button>
              </td>
              )}
              {cols.isVisible('location') && (
                <td className='whitespace-nowrap px-3 py-2.5 align-top text-xs text-gray-500'>
                  {eq.location.display}
                </td>
              )}
              {cols.isVisible('availability') && (
                <td className='whitespace-nowrap px-3 py-2.5 align-top'>
                  <AvailabilityCell
                    equipment={eq}
                    reservations={reservations}
                    unavailable={reservationsUnavailable}
                  />
                </td>
              )}
              {/* A unit of a type nobody services gets one spanning cell
                  saying so, matching the type view — rather than eight empty
                  ones inviting a click the API would refuse. The row stays in
                  the table: it is ordinary equipment with no service cycle. */}
              {eq.maintained ? (
                <>
                  {CYCLE_ORDER.filter((cycle) => cols.isVisible(cycleColumnId(cycle))).map((cycle) => (
                    <td key={cycle} className='border-l border-gray-100 px-3 py-2.5 align-top'>
                      <CycleCell
                        entry={eq.schedule[cycle]}
                        resolveMeta={resolveMeta}
                        onClick={() => onConfigure(eq, cycle)}
                        procedureFrom={scope.copy.type}
                      />
                    </td>
                  ))}
                  {cols.isVisible('calendar') && (
                    <td className='border-l border-gray-100 px-3 py-2.5 align-top'>
                      <CalendarCell info={eq.calendar} onClick={() => onCalendar(eq)} />
                    </td>
                  )}
                </>
              ) : serviceSpan > 0 ? (
                <td
                  colSpan={serviceSpan}
                  className='border-l border-gray-100 px-3 py-2.5 align-middle'
                >
                  <span className='inline-flex items-center rounded-full bg-gray-100 px-2.5 py-1 text-[11px] font-medium text-gray-500'>
                    {scope.copy.notOnCycle}
                  </span>
                </td>
              ) : null}
            </tr>
          ))}
          {rows.length === 0 && (
            <tr>
              <td colSpan={cols.visible.length} className='px-3 py-10 text-center text-sm text-gray-400'>
                {scope.copy.noMatch}
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
    </>
  )
}
